import { Button, Collapsible, Icon, IndexTable, Text, Tooltip } from '@shopify/polaris';
import {
    AlertCircleIcon,
    ChartPopularIcon,
    CheckCircleIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    QuestionCircleIcon,
} from '@shopify/polaris-icons';
import Language from '../../../../../i18n/Language';
import { numToUnit } from '../extraFunc';
import { gscTooltipContent } from '../../gscMetrics';

const t = Language;

const googleLogoIcon = (
    <svg width="16" height="16" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
);

const formatCtr = (v) => `${(Number(v || 0) * 100).toFixed(1)}%`;
const formatPosition = (v) => Number(v || 0).toFixed(1);

// GSC 搜索性能模块（Search Performance）
const SearchPerformance = ({
    searchPerformanceData,
    isGscConnected,
    rankingLoading,
    rankingKeywordsList = [],
    searchPerformanceCol,
    setSearchPerformanceCol,
    gscConnecting,
    handleConnectGsc,
    titleValue,
    metaTitleValue,
    metaDescriptionValue,
    descValue,
}) => {
    // GSC 搜索性能数据状态：
    //  - connect  未链接GSC，需先链接
    //  - fetching 已链接，数据拉取/同步中
    //  - data     已链接且有数据
    //  - empty    已链接但暂无数据
    const spCurrent = searchPerformanceData?.current || {};
    const spPrevious = searchPerformanceData?.previous || {};
    const spStatus = !isGscConnected
        ? 'connect'
        : rankingLoading
            ? 'fetching'
            : (rankingKeywordsList.length > 0 || Number(searchPerformanceData?.state) === 1)
                ? 'data'
                : Number(searchPerformanceData?.state) === 2
                    ? 'fetching'
                    : 'empty';

    // 判断 GSC 排名关键词是否已在当前页面内容中覆盖
    const contentText = `${titleValue || ''} ${metaTitleValue || ''} ${metaDescriptionValue || ''} ${(typeof descValue === 'string' ? descValue : '').replace(/<[^>]*>/g, ' ')}`.toLowerCase();
    const isKeywordCovered = (kw) => Boolean(kw) && contentText.includes(String(kw).toLowerCase());

    // 汇总指标配置（key 与 GSC 数据字段一致，用于 hover 展示 28d 环比）
    const spMetricConfig = [
        { key: 'clicks', label: t("Clicks"), format: (v) => numToUnit(v || 0) },
        { key: 'impressions', label: t("Impression"), format: (v) => numToUnit(v || 0) },
        { key: 'ctr', label: t("CTR"), format: formatCtr },
        { key: 'position', label: t("Position"), format: formatPosition },
    ];

    return (
        <div className='search_performance collect_data'>
            <div className='target_keyword_coverage_item collect_data_item' onClick={() => setSearchPerformanceCol(!searchPerformanceCol)}>
                <div className='target_keyword_coverage_item_info'>
                    <div className='flex_layout'>
                        <Text as='h2' variant='headingMd'>{t("Search Performance")}</Text>
                        <Tooltip content={t("Google Search Console performance data for this page over the last {{v1}} days.", 28)}>
                            <span className='flex_layout'><Icon source={QuestionCircleIcon} tone="subdued" /></span>
                        </Tooltip>
                    </div>
                    {
                        spStatus === 'data' ? (
                            <div className='flex_layout'>
                                <span className='flex_layout'><Icon source={ChartPopularIcon} tone='subdued' /></span>
                                <Text as='p' variant='bodySm' tone='subdued'>{t("{{v1}} Impr | {{v2}} clicks", numToUnit(spCurrent.impressions || 0), numToUnit(spCurrent.clicks || 0))}</Text>
                            </div>
                        ) : spStatus === 'connect' ? (
                            <div className='flex_layout'>
                                <span className='flex_layout'><Icon source={AlertCircleIcon} tone='warning' /></span>
                                <Text as='p' variant='bodySm' tone='subdued'>{t("GSC connected required")}</Text>
                            </div>
                        ) : (
                            <div className='flex_layout'>
                                <span className='flex_layout'><Icon source={AlertCircleIcon} tone='warning' /></span>
                                <Text as='p' variant='bodySm' tone='subdued'>{t("No data yet")}</Text>
                            </div>
                        )
                    }
                </div>
                <div className='flex_layout'>
                    <Button variant='tertiary' icon={searchPerformanceCol ? ChevronUpIcon : ChevronDownIcon}></Button>
                </div>
            </div>
            <Collapsible
                id='search_performance_collapsible'
                open={searchPerformanceCol}
                transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
                expandOnPrint
            >
                <div className='search_performance_container'>
                    {
                        spStatus === 'connect' ? (
                            <div className='search_performance_empty'>
                                <img className='sp_site_bg' src="/imgs/other/sp_site_notlink.png" alt="" />
                                <Text as='p' variant='bodyMd' tone='subdued' alignment='center'>{t("Connect GSC to view ranking queries.")}</Text>
                                <div className='flex_layout'>
                                    <Button variant='primary' loading={gscConnecting} onClick={handleConnectGsc}>
                                        <span className='sp_connect_btn'>
                                            {googleLogoIcon}
                                            <span>{t("Connect now")}</span>
                                        </span>
                                    </Button>
                                </div>
                            </div>
                        ) : spStatus === 'fetching' ? (
                            <div className='search_performance_empty'>
                                <img className='sp_site_bg' src="/imgs/other/sp_site_notdata.png" alt="" />
                                <Text as='p' variant='bodyMd' tone='subdued' alignment='center'>{t("Fetching search performance data from Google Search Console...")}</Text>
                            </div>
                        ) : spStatus === 'empty' ? (
                            <div className='search_performance_empty'>
                                <img className='sp_site_bg' src="/imgs/other/sp_site_notdata.png" alt="" />
                                <Text as='p' variant='bodyMd' fontWeight='medium' alignment='center'>{t("No search data for this page yet")}</Text>
                                {/* <Text as='p' variant='bodyMd' tone='subdued' alignment='center'>{t("New pages often take 1-7 days to start showing in search results.")}</Text> */}
                            </div>
                        ) : (
                            <div className='search_performance_data'>
                                <div className='search_performance_metrics'>
                                    {
                                        spMetricConfig.map(({ key, label, format }) => (
                                            <div className='sp_metric' key={key}>
                                                <Tooltip width='wide' content={Number(spCurrent[key]) === 0 ? t("No search data for this page yet. New pages often take 1-7 days to start showing in search results.") : gscTooltipContent(key, spCurrent[key], spPrevious[key])}>
                                                    <Text as='p' variant='bodySm' tone='subdued'>{label}</Text>
                                                    <span className='sp_metric_value table_perc_view'>
                                                        {/* {gscCellTrend(key, spCurrent[key], spPrevious[key])} */}
                                                        <Text as='span' variant='headingSm'>{format(spCurrent[key])}</Text>
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        ))
                                    }
                                </div>
                                <div className='search_performance_table'>
                                    <IndexTable
                                        selectable={false}
                                        itemCount={rankingKeywordsList.length}
                                        resourceName={{ singular: t("keyword"), plural: t("keywords") }}
                                        headings={[
                                            { title: t("Keyword") },
                                            { title: t("Clicks"), alignment: 'end' },
                                            { title: t("Impr"), alignment: 'end' },
                                        ]}
                                    >
                                        {
                                            rankingKeywordsList.map((item, index) => {
                                                const covered = isKeywordCovered(item.keyword);
                                                return (
                                                    <IndexTable.Row id={`${item.id}`} key={item.id} position={index}>
                                                        <IndexTable.Cell>
                                                            <Tooltip content={covered ? t("This keyword is already covered on this page.") : t("Not currently targeted on this page. Consider adding it naturally to the content.")}>
                                                                <div className='sp_kw_cell'>
                                                                    <span className='flex_layout sp_cover_icon'>
                                                                        {
                                                                            covered ? <Icon source={CheckCircleIcon} tone='success' />
                                                                                : <span className='sp_uncovered_dot' />
                                                                        }
                                                                    </span>
                                                                    <Text as='span' variant='bodyMd' truncate>{item.keyword}</Text>
                                                                </div>
                                                            </Tooltip>
                                                        </IndexTable.Cell>
                                                        <IndexTable.Cell>
                                                            <Text as='span' variant='bodyMd' numeric alignment='end'>{numToUnit(item.clicks || 0)}</Text>
                                                        </IndexTable.Cell>
                                                        <IndexTable.Cell>
                                                            <Text as='span' variant='bodyMd' numeric alignment='end'>{numToUnit(item.impressions || 0)}</Text>
                                                        </IndexTable.Cell>
                                                    </IndexTable.Row>
                                                );
                                            })
                                        }
                                    </IndexTable>
                                </div>
                            </div>
                        )
                    }
                </div>
            </Collapsible>
        </div>
    );
};

export default SearchPerformance;
